import { useMemo, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '@/utils';
import Icon from '@components/common/Icon';
import Button from '@components/Button/Button';
import { useAuth } from '@context/AuthContext';

interface HeaderProps {
    onToggleSidebar: () => void;
}

const getInitials = (name: string) =>
    name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);


const Header = ({ onToggleSidebar }: HeaderProps) => {
    const { t } = useTranslation('common');
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const today = useMemo(formatDate, []);

    const roleName = user?.role_name ?? '';
    const rawName = user?.name || user?.user_code || 'User';

    let displayName = rawName;
    let displaySubtitle = roleName;

    const isScopeAll = ['Admin', 'Admin IT', 'VP Sales', 'VP Service', 'Service Strategy', 'Others', 'Super Admin'].includes(roleName);
    const isZSH = roleName === 'ZSH';
    const isCluster = ['CBM Sales', 'CBM Services', 'CBM Service'].includes(roleName);

    if (isZSH && user?.zone_name) {
        displayName = user.zone_name;
        displaySubtitle = roleName;
    } else if (isCluster && user?.zone_name && user?.cluster_name) {
        displayName = `${user.zone_name} - ${user.cluster_name}`;
        displaySubtitle = roleName;
    } else if (isScopeAll) {
        // "username if exist otherwise code and bracket (superadmin)"
        displayName = `${rawName} (${roleName})`;
        displaySubtitle = ''; // Hide subtle second line
    } else {
        // Default behaviour for Dealer, ASM, CCM, Auditor, etc
        displayName = rawName;
        let locationSuffix = '';
        if (user?.zone_name && user?.cluster_name) {
            locationSuffix = ` - ${user.zone_name} (${user.cluster_name})`;
        } else if (user?.zone_name) {
            locationSuffix = ` - ${user.zone_name}`;
        }
        displaySubtitle = `${roleName}${locationSuffix}`;
    }

    const initials = rawName ? getInitials(rawName) : '?';

    const [profileOpen, setProfileOpen] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const profileRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
                setProfileOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleLogout = async () => {
        setIsLoggingOut(true);
        setProfileOpen(false);
        await logout();
        navigate('/admin/signin', { replace: true });
    };

    return (
        <header className="h-[50px] 2xl:h-[79px] bg-white border-b border-[var(--neutral-color)] flex items-center justify-between px-2 md:px-4 py-2 2xl:p-5 flex-shrink-0 z-30 transition-all duration-300">
            <div className="flex items-center gap-3 2xl:gap-5">
                <Button
                    type="button"
                    variant="ghost"
                    aria-label="Toggle sidebar"
                    onClick={onToggleSidebar}
                    className="!w-7 2xl:!w-9 !h-7 2xl:!h-9 !p-0 border-2 border-[var(--neutral-color)]"
                >
                    <Icon name="menu" className='w-[13px] 2xl:w-[18px] h-auto' alt="Menu" />
                </Button>
                <span className="hidden lg:block text-sm 2xl:text-base font-medium text-[var(--disabled-text-color)]">{today}</span>
            </div>
            <div className="flex items-center gap-2 2xl:gap-3">
                {/* <div className="hidden lg:block w-[240px] 2xl:w-[340px] [&>div]:mb-0">
                    <Input
                        type="text"
                        placeholder="Search dealers, audits, users..."
                        leftElement={searchIcon}
                    />
                </div> */}
                <div ref={profileRef} className="relative">
                    <Button
                        type="button"
                        variant="ghost"
                        onClick={() => setProfileOpen(prev => !prev)}
                        className="!flex !items-center !gap-2 2xl:!gap-2.5 !px-1 !py-1 !rounded-xl !transition-colors"
                        aria-label="User profile menu"
                        aria-expanded={profileOpen}
                    >
                        <div className="w-6 2xl:w-7.5 h-6 2xl:h-7.5 rounded-full [background:var(--primary-gradient-color)] text-white text-4 font-bold flex items-center justify-center flex-shrink-0 tracking-wide">
                            {initials}
                        </div>
                        <div className="hidden md:flex flex-col gap-0.5 text-left">
                            <span className="text-[12px] 2xl:text-[14px] font-medium text-[var(--secondary-color)] leading-tight">{displayName}</span>
                            {displaySubtitle && (
                                <span className="text-[10px] 2xl:text-[12px] text-[var(--subtitle-color)] leading-none truncate max-w-[120px] lg:max-w-[200px]" title={displaySubtitle}>{displaySubtitle}</span>
                            )}
                        </div>
                        <Icon
                            name="chevronDown"
                            alt=""
                            className={`w-2.5 2xl:w-3 h-autotransition-transform duration-200 ${profileOpen ? 'rotate-180' : ''}`}
                        />
                    </Button>
                    {profileOpen && (
                        <div className="absolute right-0 top-[calc(100%+8px)] w-38 2xl:w-48 bg-white border border-[var(--neutral-color)] rounded-2xl z-50 overflow-hidden">
                            <div className="flex flex-col min-w-0 px-3 2xl:px-4 py-2 2xl:py-3 border-b border-[var(--primary-surface-color)]">
                                <span className="text-[11px] 2xl:text-[12px] font-semibold text-[var(--secondary-color)] leading-tight truncate">
                                    {displayName}
                                </span>
                                <span className="text-[11px] 2xl:text-[12px] text-[var(--subtitle-color)] leading-tight truncate">
                                    {user?.email ?? ''}
                                </span>
                            </div>
                            <Button
                                type="button"
                                variant="ghost"
                                fullWidth
                                className="!justify-start !px-3 2xl:!px-4 !py-1.5 2xl:!py-2.5 !text-[11px] 2xl:!text-[14px] !text-[var(--secondary-color)] !font-normal hover:!bg-[var(--primary-surface-color)] !rounded-none border-b !border-[var(--primary-surface-color)]"
                                onClick={() => setProfileOpen(false)}
                            >
                                {t('header.profile_settings')}
                            </Button>
                            <Button
                                type="button"
                                variant="ghost"
                                fullWidth
                                disabled={isLoggingOut}
                                className="!justify-start !px-3 2xl:!px-4 !py-1.5 2xl:!py-2.5 !text-[11px] 2xl:!text-[14px] !text-[var(--semantic-error-color)] !font-normal hover:!bg-[var(--error-canvas-color)] !rounded-none"
                                onClick={handleLogout}
                            >
                                {isLoggingOut ? 'Signing out...' : t('header.sign_out')}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};

export default Header;